import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, CardContent, Chip, CircularProgress, Container, Grid, Stack, Typography } from '@mui/material';
import { ArrowForward, Description, Gavel, VerifiedUser } from '@mui/icons-material';
import conveyancerService from '../../services/conveyancerService';
import { brand, text } from '../../theme/colors';

const LSZ_LABEL = {
  approved: 'In good standing with the Law Society', pending: 'Law Society check pending',
  missing: 'Law Society entry not linked', rejected: 'Not in the current Law Society directory',
};
const LSZ_COLOR = { approved: 'success', pending: 'info', missing: 'warning', rejected: 'error' };

const ConveyancerDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    conveyancerService.dashboard()
      .then((res) => { if (res.success) setData(res.data); })
      .catch(() => setError('Failed to load your dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: brand.primary }} /></Container>;

  const checklist = data?.documentChecklist;
  const lsz = data?.lsz;
  const missing = (checklist?.items || []).filter((i) => !i.optional && i.status !== 'approved');
  const entry = data?.profile?.practitioner;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary, mb: 0.5 }}>
        {data?.profile?.display_name ? `Welcome, ${data.profile.display_name}` : 'Conveyancer dashboard'}
      </Typography>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 3 }}>
        Buyers find you once your account is approved and the Law Society lists you as a conveyancer.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <VerifiedUser sx={{ color: brand.primary }} />
                <Typography sx={{ fontWeight: 600 }}>Application status</Typography>
              </Stack>
              <Chip size="small" label={data?.approvalStatus || 'pending'} color={data?.approvalStatus === 'approved' ? 'success' : 'warning'} sx={{ textTransform: 'capitalize', mb: 1 }} />
              {lsz && <Chip size="small" sx={{ ml: 1, mb: 1 }} color={LSZ_COLOR[lsz.state] || 'default'} label={LSZ_LABEL[lsz.state] || lsz.state} />}
              {checklist && (
                <Typography variant="body2" sx={{ color: text.secondary, mt: 1 }}>
                  {checklist.satisfiedCount}/{checklist.requiredCount} requirements approved
                  {missing.length > 0 && ` — outstanding: ${missing.map((m) => m.label).join(', ')}`}
                </Typography>
              )}
              {data?.approvalNotes && <Alert severity="warning" sx={{ mt: 2 }}>{data.approvalNotes}</Alert>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Gavel sx={{ color: brand.primary, mb: 1 }} />
              <Typography sx={{ fontWeight: 600 }}>Profile &amp; Law Society entry</Typography>
              <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
                {entry ? `${entry.surname}${entry.first_names ? `, ${entry.first_names}` : ''}${entry.firm ? ` — ${entry.firm}` : ''}` : 'Pick your entry in the Law Society directory'}
              </Typography>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/conveyancer/profile')}>Edit profile</Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Description sx={{ color: brand.primary, mb: 1 }} />
              <Typography sx={{ fontWeight: 600 }}>Documents</Typography>
              <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>ID and this year&apos;s practising certificate</Typography>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/conveyancer/documents')}>Manage documents</Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ConveyancerDashboard;
